import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import sanitizeFileName from "helpers/sanitizeFileName";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface PromoItem {
  id?: number;
  geo: string;
  title: string;
  slug: string;
  anchor: string;
  imageBig: string;
  imageSmall: string;
  isActive: boolean;
  startedAt: string;
  finishedAt: string;
  type: string;
  promoLabel: string;
}
export interface PromosBunchPayload {
  ids: number[];
  isActive: number;
}
export interface PromoItemPayload {
  id?: number;
  country: string;
  title: string;
  slug: string;
  anchor: string;
  imageBig: string | File;
  imageSmall: string | File;
  isActive: boolean;
  startedAt: string;
  finishedAt: string;
  type: string;
  promoLabel: string;
}

interface PromoState {
  promoList: PromoItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  isItemFetched: boolean;
  isItemFetching: boolean;
  selectedItems: number[];
  payload: PromoItemPayload;
  processingItemId?: PromoItem["id"];
}
interface GetListPayload {
  limit: number;
  offset: number;
  isActive?: number;
  geos: string[];
  title: string;
  order: {
    title: string;
    geo: string;
    id: string;
    isActive: string;
  };
}

const defPayload: PromoItemPayload = {
  title: "",
  slug: "",
  anchor: "",
  country: "",
  imageBig: "",
  imageSmall: "",
  startedAt: "",
  finishedAt: "",
  isActive: true,
  type: "all",
  promoLabel: "",
};
const initialState: PromoState = {
  promoList: [],
  isListFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  isItemFetched: false,
  isItemFetching: false,
  payload: defPayload,
};

export const createPromo = createAsyncThunk<
  number,
  PromoItemPayload,
  { rejectValue: ApiErrorPayload }
>(
  "promo/create",
  async (payload: PromoItemPayload, { rejectWithValue }) => {
    const data = new FormData();
    if (payload.imageBig instanceof File) {
      const safeName = sanitizeFileName(payload.imageBig.name);

      data.append(
        "imageBig",
        new File([payload.imageBig], safeName, { type: payload.imageBig.type }),
      );
    }
    if (payload.imageSmall instanceof File) {
      const safeName = sanitizeFileName(payload.imageSmall.name);

      data.append(
        "imageSmall",
        new File([payload.imageSmall], safeName, {
          type: payload.imageSmall.type,
        }),
      );
    }
    data.append("title", payload.title as string);
    data.append("promoLabel", payload.promoLabel as string);
    data.append("slug", payload.slug as string);
    data.append("anchor", payload.anchor as string);
    data.append("country", payload.country as string);
    data.append("type", payload.type as string);
    data.append("startedAt", payload.startedAt as string);
    data.append("finishedAt", payload.finishedAt as string);
    data.append("isActive", payload.isActive ? "1" : "0");
    try {
      const result = await instance.post(`${apiRoutes.promoCreate}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return result.data.id;
    } catch (error: any) {
      const apiError = error?.data?.error;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

export const updatePromo = createAsyncThunk<
  boolean,
  PromoItemPayload,
  { rejectValue: ApiErrorPayload }
>("promo/update", async (payload: PromoItemPayload, { rejectWithValue }) => {
  const data = new FormData();

  if (payload.imageBig instanceof File) {
    const safeName = sanitizeFileName(payload.imageBig.name);

    data.append(
      "imageBig",
      new File([payload.imageBig], safeName, { type: payload.imageBig.type }),
    );
  }
  if (payload.imageSmall instanceof File) {
    const safeName = sanitizeFileName(payload.imageSmall.name);

    data.append(
      "imageSmall",
      new File([payload.imageSmall], safeName, {
        type: payload.imageSmall.type,
      }),
    );
  }
  data.append("title", payload.title as string);
  data.append("promoLabel", payload.promoLabel as string);
  data.append("slug", payload.slug as string);
  data.append("anchor", payload.anchor as string);
  data.append("country", payload.country as string);
  data.append("type", payload.type as string);
  data.append("startedAt", payload.startedAt as string);
  data.append("finishedAt", payload.finishedAt as string);
  data.append("isActive", payload.isActive ? "1" : "0");
  try {
    await instance.put(`${apiRoutes.promoUpdate}/${payload.id}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return true;
  } catch (error: any) {
    const data = error?.data;
    const apiError = data?.error ?? data;

    if (apiError && apiError.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});
export const updatePromosBunch = createAsyncThunk(
  "cms-promo/update-bunch",
  async (data: PromosBunchPayload) => {
    try {
      await instance.put(`${apiRoutes.updatePromosBunch}`, {
        ...data,
        isActive: data.isActive,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
);
export const getPromoList = createAsyncThunk(
  "promo/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.promoList}`, data);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const getPromoItem = createAsyncThunk(
  "promo/item",
  async (id: string) => {
    try {
      const result = await instance.get(`${apiRoutes.promoItem}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const deletePromo = createAsyncThunk(
  "promo/delete",
  async (id: string | number) => {
    try {
      const result = await instance.delete(`${apiRoutes.promoDelete}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);export const setPromoLocale = createAsyncThunk<
  boolean,
  {
    id: string;
    data: {
      geo: string;
      locale: string;
      title?: string | null;
      content?: string | null;
      isActive: boolean;
      promoLabel?: string | null;
    };
  },
  { rejectValue: ApiErrorPayload }
>("promo/setLocale", async ({ id, data }, { rejectWithValue }) => {
  try {
    await instance.put(`${apiRoutes.setPromoItemLocale}/${id}`, {
      ...data,
      isActive: data.isActive ? 1 : 0,
    });

    return true;
  } catch (error: any) {
    const apiError = error?.response?.data?.error || error?.data?.error;

    if (apiError?.code) {
      return rejectWithValue(apiError);
    }

    return rejectWithValue({
      code: "InternalError",
      fieldErrors: [],
      globalErrors: [{ code: "unknownError" }],
    });
  }
});



export const promoSlice = createSlice({
  name: "promo",
  initialState,
  reducers: {
    handleUpdateFields: (
      state: PromoState,
      action: PayloadAction<PromoItemPayload>,
    ) => {
      state.payload = { ...state.payload, ...action.payload };
    },
    handleClearFields: (state: PromoState) => {
      state.payload = defPayload;
      state.isItemFetched = false;
    },
    handlePaginate: (state: PromoState, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleSelectItem: (state: PromoState, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: PromoState) => {
      if (state.selectedItems.length === state.promoList.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.promoList.map((el) => el.id ?? 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPromoList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.promoList = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(createPromo.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createPromo.fulfilled, (state) => {
      state.isListFetched = false;
      state.isItemFetching = false;
    });
    builder.addCase(createPromo.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updatePromo.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updatePromo.fulfilled, (state, action) => {
      state.promoList = state.promoList.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, {
              ...action.meta.arg,
              geo: action.meta.arg.country,
              imageBig:
                action.meta.arg.imageBig instanceof File
                  ? item.imageBig
                  : action.meta.arg.imageBig,
              imageSmall:
                action.meta.arg.imageSmall instanceof File
                  ? item.imageSmall
                  : action.meta.arg.imageSmall,
            })
          : item,
      );
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updatePromo.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updatePromosBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
    builder.addCase(getPromoItem.fulfilled, (state, action) => {
      const transformPromoResponse = (data: any): PromoItemPayload => {
        return {
          ...data,
          country: data?.geo,
          isActive: !!data?.isActive,
        };
      };
      state.isItemFetched = true;
      state.payload = transformPromoResponse(action.payload);
    });
    builder.addCase(deletePromo.pending, (state, action) => {
      state.processingItemId = +action.meta.arg || 0;
    });
    builder.addCase(deletePromo.fulfilled, (state) => {
      state.isListFetched = false;
      state.processingItemId = undefined;
    });
    builder.addCase(deletePromo.rejected, (state) => {
      state.isListFetched = false;
      state.processingItemId = undefined;
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleClearFields,
  handleUpdateFields,
} = promoSlice.actions;
